import { NextPage } from 'next'
import Head from 'next/head'
import { useState, useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import { useAccount } from 'wagmi'
import {
  TransactionCallback,
  TransactionCallbackStage,
} from '@lidofinance/lido-ethereum-sdk'
import { useNativeTokenBalance } from '~/state/wallet/hooks'
import {
  useLidoStakingStats,
  useLidoSDK,
  useStakeGasFee,
  useIsValidNetwork,
  useStakeLimitInfo,
} from '../hooks'
import {
  useModalOpened,
  useModalState,
  useModalToggle,
  useApprovalModalStateDispatch,
} from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useIsDisableTrade } from '~/state/dex/hooks'
import { ApprovalProgress } from '~/components/ApprovalModal/ApproveStatusLabel'
import { formatEther, parseEther } from 'viem'
import Footer from '~/components/Footer'
import Header from '~/components/Header'
import FormHeader from '../components/FormHeader'
import Tabs from '../components/Tabs'
import TokenInput from '../components/TokenInput'
import PrimaryButton from '~/components/Button/PrimaryButton'
import OrderProgress from '../components/OrderProgress'
import BrandWatermark from '../components/BrandWatermark'
import FaqSection from '../components/FaqSection'
import MaxButton from '~/components/Button/MaxButton'
import NotificationBar from '~/components/NotificationBar'
import Tooltip from '~/components/Tooltip'
import { ETH } from '~/constants/tokens'
import { formatFormAmount, toBigNumber, gt } from '~/utils/numbers'
import { REFERRAL_ADDRESS, RESET_STAGE_TIME } from '../constants'
import {
  StyledStakingPage,
  StyledContent,
  StyledStakingBox,
} from '../../styled'
import {
  StyledStakeSection,
  StyledStakeDescription,
  StyledStakeForm,
  StyledFormGroup,
  StyledLabel,
  StyledBalanceInfo,
  StyledReceiveInfo,
  StyledInfoRow,
  StyledInfoLabel,
  StyledInfoValue,
} from '../styled'
import ImgTokenStETH from '~/assets/images/token-steth-with-bg.png'
import IconQuestionMark from '~/assets/images/question-mark-gray.svg'
import ImgLidoStakeFlow from '~/assets/images/lido-stake-flow.png'
import { lidoServiceWarningId } from '~/components/Modals'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { useGA } from '~/hooks'
import { getIsUserCancel, getFromParam } from '../utils'

const faqItems = [
  {
    id: 'lido-stake-faq-1',
    question: 'lido_stake_faq.question_1',
    answer: 'lido_stake_faq.answer_1',
  },
  {
    id: 'lido-stake-faq-2',
    question: 'lido_stake_faq.question_2',
    answer: 'lido_stake_faq.answer_2',
  },
  {
    id: 'lido-stake-faq-3',
    question: 'lido_stake_faq.question_3',
    answer: 'lido_stake_faq.answer_3',
  },
]

const LidoStake: NextPage = () => {
  const { t } = useTranslation()
  const { trackView, trackClick } = useGA()
  const router = useRouter()
  const { address: account } = useAccount()
  const isWalletConnected = !!account
  const isValidNetwork = useIsValidNetwork()
  const [amount, setAmount] = useState('')
  const [
    currentStage,
    setCurrentStage,
  ] = useState<TransactionCallbackStage | null>(null)
  const [showOrderProgress, setShowOrderProgress] = useState(false)
  const ethBalance = useNativeTokenBalance()
  const formattedEthBalance = ethBalance?.toDecimalsFixed()

  const lidoSDK = useLidoSDK()
  const { apr } = useLidoStakingStats()
  const { gasFee, gasFeeUSD } = useStakeGasFee(amount)

  const isInsufficientBalance = useMemo(() => {
    return gt(amount, formattedEthBalance)
  }, [amount, formattedEthBalance])

  const isInsufficientETHForGas = useMemo(() => {
    if (!amount || !formattedEthBalance || !gasFee) {
      return false
    }
    const ethBalanceBN = toBigNumber(formattedEthBalance)
    const totalCost = toBigNumber(amount).plus(toBigNumber(gasFee))
    return gt(totalCost, ethBalanceBN)
  }, [amount, formattedEthBalance, gasFee])

  const stakeLimitInfo = useStakeLimitInfo()
  const currentStakeLimit = stakeLimitInfo?.currentStakeLimit
    ? formatEther(stakeLimitInfo.currentStakeLimit)
    : undefined
  const isExceedStakeLimit = useMemo(() => {
    if (!currentStakeLimit) {
      return false
    }
    return gt(amount, currentStakeLimit)
  }, [amount, currentStakeLimit])

  const isApprovalModalOpen = useModalOpened(ApplicationModal.APPROVAL_STATUS)
  const { progress } = useModalState(ApplicationModal.APPROVAL_STATUS)
  const toggleSubmitParamsModal = useModalToggle(
    ApplicationModal.APPROVAL_STATUS,
    {
      stack: true,
      modalState: {
        title: t('submitParamsModal.title'),
        description1: t('submitParamsModal.description_1'),
        description2: t('submitParamsModal.description_2'),
        showLogo: false,
        progress: ApprovalProgress.APPROVING,
      },
    },
  )
  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT, {
    modalState: { from: 'lido_stake' },
  })
  const toggleSwitchNetworkModal = useModalToggle(
    ApplicationModal.SWITCH_NETWORK,
  )
  const updateApprovalState = useApprovalModalStateDispatch()

  const isDisableTrade = useIsDisableTrade()
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const toggleThirdPartyServiceWarningModal = useModalToggle(
    ApplicationModal.THIRD_PARTY_SERVICE_WARNING,
  )
  const [warningVisited, setWarningVisited] = useLocalStorage(
    lidoServiceWarningId,
    false,
  )

  const handleMaxClick = () => {
    let maxAmount = formattedEthBalance
    if (formattedEthBalance && gasFee) {
      const ethBalance = toBigNumber(formattedEthBalance)
      const remainingBalance = ethBalance.minus(gasFee)
      maxAmount = remainingBalance.gt(0) ? remainingBalance.toFixed() : '0'
    }
    setAmount(maxAmount)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // 最多输入 6 位小数
    if (value === '' || /^(\d+)(\.\d{0,6})?$/.test(value)) {
      setAmount(value)
    }
  }

  const handleBlur = () => {
    if (!amount) return
    // 去掉前导零
    let [int, dec] = amount.split('.')
    int = String(Number(int))
    setAmount(dec !== undefined ? `${int}.${dec}` : int)
  }

  const stakeValueRef = useRef<string>('0')
  const showOrderProgressRef = useRef(showOrderProgress)
  showOrderProgressRef.current = showOrderProgress

  const callback: TransactionCallback = ({ stage }) => {
    const targetStage = [
      TransactionCallbackStage.RECEIPT,
      TransactionCallbackStage.DONE,
      TransactionCallbackStage.MULTISIG_DONE,
      TransactionCallbackStage.ERROR,
    ]
    if (targetStage.includes(stage)) {
      setCurrentStage(stage)
    }

    switch (stage) {
      case TransactionCallbackStage.RECEIPT:
        trackClick('webtl_stk_stake_submit', {
          symbol: ETH.symbol,
          amount: stakeValueRef.current,
        })
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_SUCCESS,
        })
        setShowOrderProgress(true)
        break
      case TransactionCallbackStage.DONE:
      case TransactionCallbackStage.MULTISIG_DONE:
        // 使用 Safe Wallet 时没有 RECEIPT 阶段，直接从 SIGN 阶段跳到 MULTISIG_DONE 阶段
        if (
          progress !== ApprovalProgress.APPROVE_SUCCESS &&
          !showOrderProgressRef.current
        ) {
          trackClick('webtl_stk_stake_submit', {
            symbol: ETH.symbol,
            amount: stakeValueRef.current,
          })
          updateApprovalState({
            progress: ApprovalProgress.APPROVE_SUCCESS,
          })
          setShowOrderProgress(true)
        }

        trackClick('webtl_stk_stake_confirm', {
          symbol: ETH.symbol,
          amount: stakeValueRef.current,
          is_success: true,
        })
        stakeValueRef.current = '0'

        setTimeout(() => {
          setAmount('')
          setShowOrderProgress(false)
          setCurrentStage(null)
        }, RESET_STAGE_TIME)
        break
      default:
        break
    }
  }

  const handleStake = async () => {
    try {
      setCurrentStage(TransactionCallbackStage.SIGN)
      const value = parseEther(amount)
      stakeValueRef.current = amount

      if (!isApprovalModalOpen) {
        toggleSubmitParamsModal()
      }

      await lidoSDK.stake.stakeEth({
        value,
        referralAddress: REFERRAL_ADDRESS,
        callback,
      })
    } catch (error) {
      const isUserCancel = getIsUserCancel(error)
      if (isUserCancel) {
        trackView('webtl_stk_stake_confirm_cancel')
      } else {
        trackClick('webtl_stk_stake_confirm', {
          symbol: ETH.symbol,
          amount: stakeValueRef.current,
          is_success: false,
          message: error.message,
        })
      }
      stakeValueRef.current = '0'

      updateApprovalState({
        progress: ApprovalProgress.APPROVE_REJECTED,
      })
      setTimeout(() => {
        setShowOrderProgress(false)
        setCurrentStage(null)
      }, RESET_STAGE_TIME)
    }
  }

  const handleConnectWallet = () => {
    trackClick('webtl_walletconnect', {
      from: 'lido_stake',
    })
    toggleWalletModal()
  }

  const handleClick = () => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
    } else if (!isWalletConnected) {
      handleConnectWallet()
    } else if (!isValidNetwork) {
      toggleSwitchNetworkModal()
    } else {
      handleStake()
    }
  }

  const buttonText = useMemo(() => {
    if (!isWalletConnected) {
      return t('connect_wallet')
    }
    if (!isValidNetwork) {
      return t('switch_network')
    }
    if (isInsufficientBalance) {
      return t('preview_$symbol_insufficient_balance', { symbol: 'ETH' })
    }
    if (isInsufficientETHForGas) {
      return t('preview_$symbol_insufficient_balance', { symbol: 'ETH' })
    }
    if (currentStage === null) {
      return t('stake')
    }
    return t('waiting_for_confirm')
  }, [
    isWalletConnected,
    isValidNetwork,
    isInsufficientBalance,
    isInsufficientETHForGas,
    currentStage,
    t,
  ])

  const buttonDisabled = useMemo(() => {
    return (
      isWalletConnected &&
      isValidNetwork &&
      (!Number(amount) ||
        isInsufficientBalance ||
        isInsufficientETHForGas ||
        isExceedStakeLimit ||
        currentStage === TransactionCallbackStage.SIGN)
    )
  }, [
    isWalletConnected,
    isValidNetwork,
    isInsufficientBalance,
    isInsufficientETHForGas,
    amount,
    currentStage,
    isExceedStakeLimit,
  ])

  useEffect(() => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
    }
  }, [isDisableTrade])

  useEffect(() => {
    trackView('webtl_stk_detail', {
      type: 'stake',
      from: getFromParam(router.query),
    })

    if (!warningVisited && !isDisableTrade) {
      toggleThirdPartyServiceWarningModal()
      setWarningVisited(true)
    }
  }, [])

  return (
    <StyledStakingPage>
      <Head>
        <title>{t('meta_staking_title')}</title>
        <meta name="description" content={t('meta_staking_description')} />
        <meta name="keywords" content={t('meta_staking_keywords')} />
      </Head>
      <Header />
      <StyledContent>
        <StyledStakingBox root>
          <FormHeader />
          <Tabs currentTab="stake" />
          <StyledStakeSection>
            <StyledStakeDescription>
              <img
                src={ImgLidoStakeFlow.src}
                width={80}
                height={28}
                alt="stake flow"
              />
              <div className="description-text">
                {t('lido_stake_description')}
              </div>
            </StyledStakeDescription>

            <StyledStakeForm>
              <StyledFormGroup>
                {isWalletConnected && (
                  <StyledLabel>
                    <StyledBalanceInfo>
                      {`${t('balance')}: ${formatFormAmount(
                        formattedEthBalance ?? '~',
                        4,
                      )} ETH`}
                      <MaxButton handleClick={handleMaxClick} />
                    </StyledBalanceInfo>
                  </StyledLabel>
                )}
                <TokenInput
                  amount={amount}
                  token={ETH}
                  handleInputChange={handleInputChange}
                  handleBlur={handleBlur}
                />
              </StyledFormGroup>

              <StyledReceiveInfo>
                <StyledInfoRow>
                  <StyledInfoLabel>{t('order_receive')}</StyledInfoLabel>
                  <StyledInfoValue>
                    <span>{`${amount || '0.0'} stETH`}</span>
                    <img
                      src={ImgTokenStETH.src}
                      alt="stETH"
                      width={20}
                      height={20}
                    />
                  </StyledInfoValue>
                </StyledInfoRow>
                <StyledInfoRow>
                  <StyledInfoLabel>APR</StyledInfoLabel>
                  <StyledInfoValue color="primary">{apr}</StyledInfoValue>
                </StyledInfoRow>
                <StyledInfoRow>
                  <StyledInfoLabel>{t('rate')}</StyledInfoLabel>
                  <StyledInfoValue>1 ETH = 1 stETH</StyledInfoValue>
                </StyledInfoRow>
                <StyledInfoRow>
                  <StyledInfoLabel>{t('estimate_gas_fee')}</StyledInfoLabel>
                  <StyledInfoValue>{`~ $${gasFeeUSD}`}</StyledInfoValue>
                </StyledInfoRow>
                <StyledInfoRow>
                  <StyledInfoLabel>
                    {t('lido_fee')}
                    <Tooltip
                      title={t('lido_fee_tip')}
                      placement="top"
                      trigger="hover"
                      img={IconQuestionMark}
                      iconClassName="tooltip-wrap-new"
                    />
                  </StyledInfoLabel>
                  <StyledInfoValue>10%</StyledInfoValue>
                </StyledInfoRow>
              </StyledReceiveInfo>
              {showOrderProgress && <OrderProgress stage={currentStage!} />}
              {!showOrderProgress && (
                <>
                  {!isValidNetwork && (
                    <NotificationBar
                      level="warning"
                      text={t('unmatched_network_swap_warning')}
                      showLink={false}
                      showClose={false}
                    />
                  )}
                  {isExceedStakeLimit && (
                    <NotificationBar
                      level="warning"
                      text={t('lido_stake_limit_exceeded', {
                        amount: currentStakeLimit,
                      })}
                      showLink={false}
                      showClose={false}
                    />
                  )}
                  <PrimaryButton
                    text={buttonText}
                    disabled={buttonDisabled}
                    loading={currentStage === TransactionCallbackStage.SIGN}
                    handleClick={handleClick}
                  />
                </>
              )}
            </StyledStakeForm>

            <BrandWatermark />
          </StyledStakeSection>
        </StyledStakingBox>
        <FaqSection faqItems={faqItems} from="stake" />
      </StyledContent>
      <Footer />
    </StyledStakingPage>
  )
}

export default LidoStake
